import type { FC, KeyboardEventHandler, MouseEvent } from 'react';
import React, { useEffect, useRef } from 'react';
import classNames from 'clsx';
import { keyCodes } from '@wix/editor-elements-common-utils';

import type {
  AvatarWrapperProps,
  ILoginSocialBarCustomMenu,
  ILoginSocialBarProps,
  LoginSocialBarMenuItemProps,
} from '../../../../LoginSocialBar.types';
import { LoggedInTextState } from '../../../../LoginSocialBar.types';
import {
  ARIA_LABEL_MEMBERS_BAR,
  TEST_ID_HANDLE_BUTTON,
} from '../../../../constants';
import Arrow from '../arrow';
import AvatarWrapper from '../avatar';
import LoginSocialBarCustomMenu, {
  LOGIN_SOCIAL_BAR_CUSTOM_MENU_ID,
} from '../loginSocialBarCustomMenu/LoginSocialBarCustomMenu';
import LoginSocialBarIcons from '../loginSocialBarIcons';
import LoginSocialBarNativeMenu from '../loginSocialBarNativeMenu';

import styles from './LoggedInSocialBar.module.scss';
import { createMenuButtonKeyHandler } from '../../../../utils/keyboardUtils';
import { LOGIN_SOCIAL_BAR_NATIVE_MENU_ID } from '../loginSocialBarNativeMenu/LoginSocialBarNativeMenu';

type LoggedInSocialBarProps = AvatarWrapperProps &
  Pick<
    ILoginSocialBarProps,
    | 'translations'
    | 'isMenuOpen'
    | 'onMenuOpen'
    | 'onMenuClose'
    | 'menuItems'
    | 'onLogout'
    | 'currentPrimaryPageHref'
    | 'iconItems'
    | 'useNativeMenu'
    | 'navigateTo'
    | 'reportBi'
    | 'loggedInTextState'
    | 'loggedInText'
    | 'customLoggedInText'
  > & {
    renderIcons: boolean;
    reportBiOnClick: (label: string) => void;
    reportBiOnMenuItemClick: (item: LoginSocialBarMenuItemProps) => void;
    rootElement: HTMLDivElement | null;
    useNewCustomMenuPositioning: boolean;
    useNewElementStructure: boolean;
  };

export const LoggedInSocialBar: FC<LoggedInSocialBarProps> = ({
  translations,
  isMenuOpen,
  onMenuOpen,
  onMenuClose,
  menuItems,
  onLogout,
  currentPrimaryPageHref,
  id,
  iconItems,
  userName,
  avatarIcon,
  useNativeMenu,
  renderIcons,
  avatarUri,
  isLoggedIn,
  navigateTo,
  loggedInTextState,
  loggedInText: greetingText,
  customLoggedInText,
  reportBi,
  reportBiOnClick,
  reportBiOnMenuItemClick,
  customAvatar,
  useNewCustomMenuPositioning,
  rootElement,
  useNewElementStructure,
}) => {
  const toggleMenuOpen = () => (isMenuOpen ? onMenuClose() : onMenuOpen());

  const handleLogout = (event: MouseEvent) => onLogout(event);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLDivElement>(null);
  const customMenuContainerRef = useRef<ILoginSocialBarCustomMenu>(null);

  useEffect(() => {
    if (isMenuOpen && customMenuContainerRef.current) {
      customMenuContainerRef.current.focusFirstMenuItem();
    }
  }, [isMenuOpen]);

  const handleNativeMenuUserButtonKeyDown: KeyboardEventHandler = event => {
    const { keyCode } = event;
    const { enter, space, escape, arrowUp, arrowDown } = keyCodes;
    const openMenuKeys: Array<number> = [enter, space, arrowDown];
    const closeMenuKeys: Array<number> = [enter, space, escape, arrowUp];

    if (isMenuOpen) {
      if (closeMenuKeys.includes(keyCode)) {
        !useNativeMenu && event.preventDefault();
        onMenuClose();
      } else if (keyCode === arrowDown) {
        !useNativeMenu && event.preventDefault();
        if (customMenuContainerRef.current) {
          customMenuContainerRef.current.focusFirstMenuItem();
        }
      }
    } else if (openMenuKeys.includes(keyCode)) {
      !useNativeMenu && event.preventDefault();
      onMenuOpen();
    }
  };

  const handleCustomMenuUserButtonKeyDown: KeyboardEventHandler =
    createMenuButtonKeyHandler(
      isMenuOpen,
      onMenuOpen,
      onMenuClose,
      customMenuContainerRef,
    );

  const handleUserButtonKeyDown: KeyboardEventHandler = useNativeMenu
    ? handleNativeMenuUserButtonKeyDown
    : handleCustomMenuUserButtonKeyDown;

  const handleCustomMenuClose = () => {
    onMenuClose();
    if (userButtonRef.current) {
      userButtonRef.current.focus();
    }
  };

  const handleButtonClick = () => {
    reportBiOnClick(loggedInText || '');
    toggleMenuOpen();
  };

  const commonMenuProps = {
    menuItems,
    translations,
    currentPrimaryPageHref,
    reportBiOnMenuItemClick,
    onLogout: handleLogout,
  };

  const wrapperClassNames = classNames(styles.wrapper, {
    [styles.withIcons]: renderIcons,
  });

  const userButtonClassNames = classNames(styles.user, {
    [styles.nativeMenu]: useNativeMenu,
    [styles.customMenu]: !useNativeMenu,
  });

  const withMemberName = loggedInTextState === LoggedInTextState.MemberName;
  const loggedInText = withMemberName ? userName : customLoggedInText;
  const renderGreetingText = withMemberName && !!greetingText;

  return (
    <div className={wrapperClassNames} ref={wrapperRef}>
      {renderIcons && (
        <LoginSocialBarIcons
          className={styles.icons}
          containerId={id}
          iconItems={iconItems}
        />
      )}
      <div
        ref={userButtonRef}
        className={userButtonClassNames}
        data-testid={TEST_ID_HANDLE_BUTTON}
        onClick={handleButtonClick}
        onKeyDown={handleUserButtonKeyDown}
        role="button"
        tabIndex={useNativeMenu ? undefined : 0}
        aria-label={translations.accountButtonAriaLabel?.replace(
          '{userName}',
          userName || '',
        )}
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
        aria-controls={
          useNativeMenu
            ? LOGIN_SOCIAL_BAR_NATIVE_MENU_ID
            : LOGIN_SOCIAL_BAR_CUSTOM_MENU_ID
        }
      >
        <AvatarWrapper
          id={id}
          avatarIcon={avatarIcon}
          isLoggedIn={isLoggedIn}
          userName={userName}
          avatarUri={avatarUri}
          customAvatar={customAvatar}
          useNewElementStructure={useNewElementStructure}
        />
        <div className={styles.name}>
          {renderGreetingText && (
            <span className={styles.greetingText}>{greetingText} </span>
          )}
          {loggedInText}
        </div>

        {useNewElementStructure ? (
          <Arrow className={styles.arrow} useNewElementStructure={true} />
        ) : (
          <div className={styles.arrowWrapper}>
            <Arrow className={styles.arrow} useNewElementStructure={false} />
          </div>
        )}

        {useNativeMenu && (
          <LoginSocialBarNativeMenu
            {...commonMenuProps}
            ariaLabel={ARIA_LABEL_MEMBERS_BAR}
            navigateTo={navigateTo}
            reportBi={reportBi}
          />
        )}
      </div>
      {!useNativeMenu && (
        <LoginSocialBarCustomMenu
          {...commonMenuProps}
          ref={customMenuContainerRef}
          rootElement={rootElement}
          referenceElement={wrapperRef.current}
          useNewCustomMenuPositioning={useNewCustomMenuPositioning}
          ariaLabel={ARIA_LABEL_MEMBERS_BAR}
          open={isMenuOpen}
          onClose={handleCustomMenuClose}
          reportBi={reportBi}
        />
      )}
    </div>
  );
};
